// /email-preferences?t=… — the notification settings a signed footer link opens,
// with no login.
//
// WHY THIS EXISTS. Every notification email used to end in one control:
// "Unsubscribe from these emails", all-or-nothing, forever, every group. Someone
// who wanted "stop telling me about this one group" or "just not every RSVP" had
// no move except the nuclear one, so that is the one they took.
//
// The settings already existed — `notification_prefs` has been per user PER GROUP
// with six category flags since Phase 7, and /settings/notifications renders all
// of it. What was missing was the ROUTE from the email to the screen, and the
// reason it was missing is real: that screen needs a session, and the person
// holding the email may not have one on the device holding it. An unsubscribe
// link that demands a password is hostile; so is making the gentler choice the
// one that needs a login while the drastic one does not.
//
// ⚠ This page is NOT the signed-in settings screen without the sign-in. It is
// deliberately narrower — no push (per device, and unreachable from a mailed
// link), no account-wide reaction mute. Everything it shows is one more fact
// disclosed to whoever holds a forwarded message, and it already discloses more
// than the unsubscribe link does: the NAMES of every group the recipient is in.
// That is the price of "checkboxes for the groups" being the point of the page.
// The full-fidelity screen stays at /settings/notifications.

import {
  ApiClientError,
  emailPrefsByToken,
  setEmailSubscriptionByToken,
  updateEmailPrefsByToken,
} from '../api/client.js';
import type { View } from '../router.js';
import { el } from '../dom.js';
import {
  VISIBLE_NOTIFICATION_PREF_KEYS,
  NOTIFICATION_PREF_LABELS,
  type EmailPrefsResponse,
  type NotificationPrefKey,
  type NotificationPrefs,
} from '@msc/shared';

export const emailPrefsView: View = async (outlet, ctx) => {
  const token = ctx.query.get('t') ?? '';
  // Only ever used to scroll to and highlight one group. It is outside the
  // token's signature on purpose: tampering with it changes nothing a holder of
  // the token could not already see further down the same page.
  const focusGroup = Number(ctx.query.get('g') ?? '');

  const main = el('main', { class: 'page narrow' }, [
    el('h1', {}, 'Email preferences'),
    el('p', { class: 'muted' }, 'Loading'),
  ]);
  outlet.append(main);

  if (!token) {
    main.replaceChildren(
      el('h1', {}, 'Link not valid'),
      el('p', { class: 'form-error' }, 'That link is missing its code.'),
      el('p', {}, [el('a', { href: '/settings/notifications' }, 'Open notification settings')]),
    );
    return;
  }

  let data: EmailPrefsResponse;
  try {
    data = await emailPrefsByToken(token);
  } catch (err) {
    main.replaceChildren(
      el('h1', {}, 'Could not open your preferences'),
      el(
        'p',
        { class: 'form-error' },
        err instanceof ApiClientError && err.code === 'GONE'
          ? 'That link has expired or is not valid. You can change these in your settings instead.'
          : err instanceof ApiClientError
            ? err.message
            : 'Something went wrong.',
      ),
      el('p', {}, [el('a', { href: '/settings/notifications' }, 'Open notification settings')]),
    );
    return;
  }

  render(main, token, data, Number.isInteger(focusGroup) && focusGroup > 0 ? focusGroup : null);
};

function render(
  main: HTMLElement,
  token: string,
  data: EmailPrefsResponse,
  focusGroup: number | null,
): void {
  main.replaceChildren(
    el('h1', {}, 'Email preferences'),
    el('p', { class: 'muted' }, `For ${data.email}`),
    ...(data.unsubscribed ? [unsubscribedNotice(main, token)] : []),
    ...(data.groups.length === 0
      ? [el('p', { class: 'muted' }, 'You are not a member of any groups.')]
      : data.groups.map((g) => groupSection(token, g, g.groupId === focusGroup, data.unsubscribed))),
    ...(data.unsubscribed ? [] : [unsubscribeAll(main, token)]),
    el('p', { class: 'muted small' }, [
      'Account email (password resets and address verification) always sends, ' +
        'because that is how you get back into your account. ',
      el('a', { href: '/settings/notifications' }, 'Full notification settings'),
      ' are available when you are signed in.',
    ]),
  );

  if (focusGroup !== null) {
    main.querySelector(`[data-prefs-group="${focusGroup}"]`)?.scrollIntoView({ block: 'center' });
  }
}

/**
 * One group, six checkboxes.
 *
 * Each writes on its own, immediately — there is no Save button. A preferences
 * page reached by someone who wants the noise to stop is the wrong place to make
 * them find and press a second control, and every write here is trivially
 * reversible by the checkbox that made it.
 */
function groupSection(
  token: string,
  prefs: NotificationPrefs,
  focused: boolean,
  allOff: boolean,
): HTMLElement {
  const status = el('p', { class: 'muted small' });
  const rows = VISIBLE_NOTIFICATION_PREF_KEYS.map((key) => prefRow(token, prefs, key, status, allOff));

  return el(
    'section',
    {
      class: focused ? 'card prefs-group is-focused' : 'card prefs-group',
      'data-prefs-group': String(prefs.groupId),
    },
    [el('h2', {}, prefs.groupName), ...rows, status],
  );
}

function prefRow(
  token: string,
  prefs: NotificationPrefs,
  key: NotificationPrefKey,
  status: HTMLElement,
  allOff: boolean,
): HTMLElement {
  const { label, help } = NOTIFICATION_PREF_LABELS[key];
  const box = el('input', { type: 'checkbox' }) as HTMLInputElement;
  box.checked = prefs[key];
  // Every box is dead while the address is unsubscribed entirely, because the
  // account-wide opt-out wins over all of them. Leaving them live would let
  // someone tick "New events" and reasonably conclude they had turned it on.
  box.disabled = allOff;

  box.addEventListener('change', async () => {
    const wanted = box.checked;
    box.disabled = true;
    status.textContent = 'Saving…';
    try {
      const updated = await updateEmailPrefsByToken({
        token,
        groupId: prefs.groupId,
        [key]: wanted,
      });
      // Trust the server's row, not the click: a write that silently landed
      // differently should show what is actually stored.
      box.checked = updated[key];
      status.textContent = 'Saved';
    } catch (err) {
      box.checked = !wanted;
      status.textContent =
        err instanceof ApiClientError ? err.message : 'Could not save that. Try again.';
    } finally {
      box.disabled = false;
    }
  });

  // `label.check` is the existing reset for a control built on a <label>: the
  // global `input { width: 100% }` would otherwise stretch this checkbox across
  // the whole row and squeeze its text into a sliver.
  return el('label', { class: 'check' }, [
    box,
    el('span', {}, [el('strong', {}, label), el('span', { class: 'muted small' }, ` ${help}`)]),
  ]);
}

/**
 * The all-or-nothing switch, kept at the BOTTOM and kept plain.
 *
 * It stays a real, working, one-action control — the per-group boxes above are
 * an addition to the choice, never a maze in front of it. Burying an opt-out
 * behind a preferences screen is the pattern that earns a spam classification.
 */
function unsubscribeAll(main: HTMLElement, token: string): HTMLElement {
  const status = el('p', { class: 'muted small' });
  const btn = el('button', { class: 'btn-link', type: 'button' }, 'Unsubscribe from all email');

  btn.addEventListener('click', async () => {
    (btn as HTMLButtonElement).disabled = true;
    status.textContent = 'Unsubscribing…';
    try {
      const data = await setEmailSubscriptionByToken({ token, unsubscribed: true });
      render(main, token, data, null);
    } catch (err) {
      (btn as HTMLButtonElement).disabled = false;
      status.textContent =
        err instanceof ApiClientError ? err.message : 'Could not unsubscribe. Try again.';
    }
  });

  return el('section', { class: 'card' }, [
    el('h2', {}, 'All notification email'),
    el('p', { class: 'muted small' }, 'Turns off every group at once, including any added later.'),
    btn,
    status,
  ]);
}

/**
 * Shown INSTEAD of the unsubscribe control once the address is opted out.
 *
 * This is also the only way back for someone who is not signed in.
 * `POST /me/email/resubscribe` needs a session, so before this page the only
 * route was a signed link from an older email — the dead end
 * `docs/todo-email-resubscribe.md` recorded before it was deleted. Someone who
 * unsubscribed by mistake and then cleared their mail had no way back at all.
 */
function unsubscribedNotice(main: HTMLElement, token: string): HTMLElement {
  const status = el('p', { class: 'muted small' });
  const btn = el('button', { class: 'btn', type: 'button' }, 'Turn notification email back on');

  btn.addEventListener('click', async () => {
    (btn as HTMLButtonElement).disabled = true;
    status.textContent = 'Turning it back on…';
    try {
      const data = await setEmailSubscriptionByToken({ token, unsubscribed: false });
      render(main, token, data, null);
    } catch (err) {
      (btn as HTMLButtonElement).disabled = false;
      status.textContent =
        err instanceof ApiClientError ? err.message : 'Could not do that. Try again.';
    }
  });

  return el('section', { class: 'card' }, [
    el('h2', {}, 'Notification email is off'),
    el(
      'p',
      { class: 'muted small' },
      'This address is unsubscribed, so the settings below have no effect until you turn it ' +
        'back on.',
    ),
    btn,
    status,
  ]);
}
